'use client'

import React, { useEffect } from 'react'
import type { Group } from '@/features/stm/lib/types'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import { useStmUpdateGroup } from '@/features/stm/hooks/use-stm-groups-blocks'
import { PreTripTabContent } from './PreTripTabContent'
import { useRegion } from '@/shared/hooks/use-region'

/** One auto-mark attempt per group per page load (survives React Strict Mode double effect). */
const pretripAutoMarkAttemptedGroupIds = new Set<string>()

export type PreTripFlipbookFromApi = {
  slotIndex: number
  pageImageUrls: string[]
  pdfDownloadUrl: string
}

export type PreTripFromApi = {
  flipbooks: PreTripFlipbookFromApi[]
  videoUrl?: string | null
  videoThumbnailUrl?: string | null
  videoDuration?: number | null
}

type GroupPreTripTabProps = {
  group: Group
  /** Passed from parent so PreTrip has region context (same as used to load the group). */
  regionId: number | null
}

/**
 * PreTrip tab: one video card + PDF flip book cards (see PRETRIP_CONFIG).
 * Documents are stored in the region's Base Photos and persist.
 * When the Group Leader (contact for the group, shown in the Group Leader card) views this tab, Pre-Trip Info is auto-marked given.
 */
export function GroupPreTripTab({ group, regionId }: GroupPreTripTabProps) {
  const { session } = useSession()
  const { regionSlug } = useRegion()
  const updateGroup = useStmUpdateGroup()

  const canEditPreTrip =
    session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

  const showInstructionsCard = canEditPreTrip

  const isGroupLeader = Boolean(
    session?.email?.trim() &&
      group?.contact_email?.trim() &&
      session.email.trim().toLowerCase() === group.contact_email.trim().toLowerCase()
  )

  useEffect(() => {
    if (!group?.id || group.pretrip_info_given || !isGroupLeader) return
    const gid = String(group.id)
    if (pretripAutoMarkAttemptedGroupIds.has(gid)) return
    pretripAutoMarkAttemptedGroupIds.add(gid)
    updateGroup.mutate({
      id: group.id,
      pretrip_info_given: true,
      silent: true,
    })
  }, [group?.id, group?.pretrip_info_given, isGroupLeader, updateGroup])

  return (
    <PreTripTabContent
      regionId={regionId}
      regionSlug={regionSlug}
      showInstructionsCard={showInstructionsCard}
      canEditPreTrip={canEditPreTrip}
      compactVideoHeightOnDesktop
    />
  )
}
